import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  compareContributionsByRecencyDesc,
  isAdhesionContributionMonth,
} from '../utils/memberDashboardPersonalStats';
import {
  MemberMobileShell,
  MemberMobileHero,
  MemberMobileSection,
  MemberMobileList,
  MemberMobileCollapse,
} from './MemberMobileShell';

/**
 * Vue cotisations mobile-only (membre).
 * Le desktop garde sa propre composition (table / filtres).
 */
export default function MemberContributionsMobile({
  localeFmt = 'fr-FR',
  amountDue = 0,
  unpaidMonths = 0,
  deadlineDay = null,
  daysLeft = null,
  dueRows = [],
  contributions = [],
  canCreate = false,
  canOfferArrearsInvoice = false,
  getMonthName,
  onPay,
  onPayDue,
  onInvoice,
  onArrearsInvoice,
  onReceipt,
  onEdit,
}) {
  const { t } = useTranslation('common');
  const [historyOpen, setHistoryOpen] = useState(false);
  const historySeededRef = useRef(false);

  const monthLabel = (month) => (typeof getMonthName === 'function' ? getMonthName(month) : month || '—');

  const dueKeys = useMemo(() => {
    const set = new Set();
    for (const row of dueRows || []) {
      const ym = String(row?.month || '').trim().slice(0, 7);
      if (ym) set.add(ym);
    }
    return set;
  }, [dueRows]);

  const actionRows = useMemo(() => {
    const rows = [];
    for (const row of dueRows || []) {
      rows.push({
        key: `due:${String(row?.month || '').slice(0, 7)}:${row?.id || ''}`,
        kind: row?.dueState === 'overdue' ? 'overdue' : 'due',
        month: row?.month,
        amount: Number(row?.amount) || 0,
        raw: row,
      });
    }
    for (const c of contributions || []) {
      const st = String(c?.status || '').toLowerCase();
      const ym = String(c?.month || '').trim().slice(0, 7);
      if (dueKeys.has(ym) && (st === 'pending' || st === 'awaiting' || st === 'unpaid')) continue;
      if (st === 'pending' || st === 'awaiting') {
        rows.push({
          key: `pending:${c?.id || ym}`,
          kind: 'pending',
          month: c?.month,
          amount: Number(c?.amount) || 0,
          raw: c,
        });
      } else if (st === 'rejected') {
        rows.push({
          key: `rejected:${c?.id || ym}`,
          kind: 'rejected',
          month: c?.month,
          amount: Number(c?.amount) || 0,
          reason: c?.rejection_reason || '',
          raw: c,
        });
      }
    }
    const rank = { overdue: 0, due: 1, rejected: 2, pending: 3 };
    return rows.sort((a, b) => (rank[a.kind] ?? 9) - (rank[b.kind] ?? 9));
  }, [dueRows, contributions, dueKeys]);

  const historyRows = useMemo(() => {
    return (contributions || [])
      .filter((c) => {
        const st = String(c?.status || '').toLowerCase();
        return st === 'completed' || st === 'approved' || st === 'paid';
      })
      .slice()
      .sort(compareContributionsByRecencyDesc);
  }, [contributions]);

  /** Dernier paiement = plus récent hors adhésion (l’adhésion est le premier versement). */
  const lastPayment = useMemo(() => {
    const regular = historyRows.find((c) => !isAdhesionContributionMonth(c?.month));
    return regular || historyRows[0] || null;
  }, [historyRows]);

  const isUpToDate = Number(amountDue) <= 0;
  const showDeadlineHint =
    !isUpToDate && daysLeft != null && deadlineDay != null && Number(daysLeft) >= 0;

  const primaryDue = actionRows.find((r) => r.kind === 'due' || r.kind === 'overdue');

  useEffect(() => {
    if (historySeededRef.current) return;
    if (!isUpToDate && actionRows.length === 0 && historyRows.length === 0) return;
    historySeededRef.current = true;
    if (isUpToDate && historyRows.length > 0) setHistoryOpen(true);
  }, [isUpToDate, actionRows.length, historyRows.length]);

  const handlePrimaryPay = () => {
    if (primaryDue && canCreate) {
      onPayDue?.(primaryDue.raw);
      return;
    }
    onPay?.();
  };

  const borderForKind = (kind) => {
    if (kind === 'overdue' || kind === 'rejected') return 'border-l-red-500';
    if (kind === 'due') return 'border-l-amber-400';
    return 'border-l-[#2f6fd4]';
  };

  const pillForKind = (kind) => {
    if (kind === 'overdue' || kind === 'rejected') {
      return 'bg-red-50 text-red-700 ring-1 ring-red-100';
    }
    if (kind === 'due') return 'bg-amber-50 text-amber-800 ring-1 ring-amber-100';
    return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
  };

  return (
    <MemberMobileShell
      bottomPad={false}
      className={
        !isUpToDate && canCreate
          ? 'pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))]'
          : 'pb-[calc(1rem+env(safe-area-inset-bottom,0px))]'
      }
    >
      <MemberMobileHero
        label={
          isUpToDate
            ? t('contributionsPage.mobileNative.statusLabel')
            : t('contributionsPage.mobileNative.dueLabel')
        }
        title={
          isUpToDate ? (
            t('contributionsPage.mobileNative.upToDate')
          ) : (
            <>
              {Number(amountDue).toLocaleString(localeFmt)}
              <span className="ml-1.5 font-display text-sm font-bold text-amber-800/70">FBU</span>
            </>
          )
        }
        hint={
          isUpToDate
            ? t('contributionsPage.mobileNative.upToDateHint')
            : [
                unpaidMonths > 0
                  ? t('contributionsPage.mobileNative.dueHint', { count: unpaidMonths })
                  : t('contributionsPage.mobileNative.dueHintGeneric'),
                showDeadlineHint
                  ? Number(daysLeft) === 0
                    ? t('contributionsPage.deadline.mobileOverdue')
                    : t('contributionsPage.deadline.mobileShort', {
                        days: daysLeft,
                        day: deadlineDay,
                      })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
        tone={isUpToDate ? 'ok' : 'warn'}
        actions={
          isUpToDate && canCreate ? (
            <button type="button" onClick={onPay} className="mm-cta flex-1">
              {t('contributionsPage.mobileNative.ctaPay')}
            </button>
          ) : !isUpToDate && canOfferArrearsInvoice ? (
            <button
              type="button"
              onClick={onArrearsInvoice}
              className="mm-cta mm-cta--ghost flex-1 border-amber-300 text-amber-950"
            >
              {t('contributionsPage.invoice.openArrearsTiny')}
            </button>
          ) : null
        }
      >
        {isUpToDate && lastPayment ? (
          <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-x-3 gap-y-0.5 rounded-xl border border-emerald-200/70 bg-white/75 px-3 py-2.5">
            <p className="col-span-2 font-display text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-800/80">
              {t('contributionsPage.mobileNative.lastPayment')}
            </p>
            <p className="min-w-0 truncate text-sm font-bold leading-5 text-[#0f3350]">
              {monthLabel(lastPayment.month)}
            </p>
            <p className="text-right font-display text-sm font-extrabold tabular-nums leading-5 text-[#0f3350]">
              {(Number(lastPayment.amount) || 0).toLocaleString(localeFmt)}
              <span className="ml-0.5 text-[10px] font-bold text-slate-500">FBU</span>
            </p>
          </div>
        ) : null}
      </MemberMobileHero>

      {actionRows.length > 0 ? (
        <MemberMobileSection title={t('contributionsPage.mobileNative.toHandle', { count: actionRows.length })}>
          <MemberMobileList>
            {actionRows.map((row) => {
              const badge =
                row.kind === 'overdue'
                  ? t('contributionsPage.dueSection.overdue')
                  : row.kind === 'due'
                    ? t('contributionsPage.dueSection.due')
                    : row.kind === 'rejected'
                      ? t('contributionsPage.mobile.statusRejected')
                      : t('contributionsPage.mobile.statusPending');

              const primaryAction =
                (row.kind === 'due' || row.kind === 'overdue') && canCreate
                  ? {
                      label: t('contributionsPage.mobileNative.payMonth'),
                      onClick: () => onPayDue?.(row.raw),
                      solid: true,
                    }
                  : row.kind === 'rejected' && canCreate
                    ? {
                        label: t('contributionsPage.mobile.correctShort'),
                        onClick: () => onEdit?.(row.raw),
                        solid: true,
                      }
                    : row.kind === 'pending' && onReceipt
                      ? {
                          label: t('contributionsPage.receipt.open'),
                          onClick: () => onReceipt?.(row.raw),
                          solid: false,
                        }
                      : null;

              return (
                <li
                  key={row.key}
                  className={`space-y-3 border-l-4 bg-white/60 px-3.5 py-3.5 ${borderForKind(row.kind)}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#0f3350]">
                        {monthLabel(row.month)}
                      </p>
                      <p className="mt-0.5 font-display text-base font-extrabold tabular-nums text-[#0f3350]">
                        {row.amount.toLocaleString(localeFmt)}
                        <span className="ml-1 text-[10px] font-bold text-slate-500">FBU</span>
                      </p>
                      {row.kind === 'rejected' && row.reason ? (
                        <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-red-700">
                          {row.reason}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${pillForKind(row.kind)}`}
                    >
                      {badge}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {primaryAction ? (
                      <button
                        type="button"
                        onClick={primaryAction.onClick}
                        className={
                          primaryAction.solid
                            ? 'mm-cta min-h-[40px] px-3 text-xs'
                            : 'min-h-[40px] rounded-xl border border-slate-200 bg-[#edf4ff]/50 px-3 text-xs font-medium text-[#113a5b]'
                        }
                      >
                        {primaryAction.label}
                      </button>
                    ) : null}
                    {(row.kind === 'due' || row.kind === 'overdue') && onInvoice ? (
                      <button
                        type="button"
                        onClick={() => onInvoice?.(row.raw)}
                        className="min-h-[40px] rounded-xl border border-amber-200 bg-amber-50/80 px-3 text-xs font-medium text-amber-900"
                      >
                        {t('contributionsPage.dueSection.invoice')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </MemberMobileList>
        </MemberMobileSection>
      ) : !isUpToDate ? (
        <section className="mm-card border-dashed px-4 py-5 text-center">
          <p className="text-sm text-slate-600">{t('contributionsPage.mobileNative.noActions')}</p>
          {canCreate ? (
            <button
              type="button"
              onClick={onPay}
              className="mt-3 text-sm font-bold text-[#113a5b]"
            >
              {t('contributionsPage.mobileNative.ctaSettle')}
            </button>
          ) : null}
        </section>
      ) : null}

      <MemberMobileCollapse
        title={t('contributionsPage.mobileNative.history', { count: historyRows.length })}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        footer={
          <Link
            to="/transactionDetails/cotisations"
            className="mt-2 inline-flex min-h-[36px] items-center text-xs font-bold text-[#113a5b]"
          >
            {t('contributionsPage.mobileNative.allTransactions')} →
          </Link>
        }
      >
        {historyRows.length === 0 ? (
          <p className="py-3 text-center text-xs text-slate-500">
            {t('contributionsPage.mobileNative.historyEmpty')}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100/90">
            {historyRows.slice(0, 12).map((c) => (
              <li
                key={c.id || `${c.month}-${c.amount}`}
                className="flex items-start gap-3 border-l-4 border-l-[#2f6fd4]/35 py-2.5 pl-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold leading-5 text-[#0f3350]">
                    {monthLabel(c.month)}
                  </p>
                  <p className="truncate text-[11px] leading-4 text-slate-500">
                    {c.payment_method || '—'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <span className="font-display text-sm font-extrabold tabular-nums leading-5 text-[#0f3350]">
                    {(Number(c.amount) || 0).toLocaleString(localeFmt)}
                    <span className="ml-0.5 text-[10px] font-bold text-slate-500">FBU</span>
                  </span>
                  {onReceipt ? (
                    <button
                      type="button"
                      onClick={() => onReceipt?.(c)}
                      className="min-h-[32px] rounded-lg border border-slate-200 bg-[#edf4ff]/50 px-2.5 text-[11px] font-medium text-[#113a5b]"
                    >
                      {t('contributionsPage.receipt.open')}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </MemberMobileCollapse>

      {!isUpToDate && canCreate ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.35rem)] z-40 px-3">
          <button
            type="button"
            onClick={handlePrimaryPay}
            className="mm-cta pointer-events-auto flex min-h-[52px] w-full gap-2 text-sm shadow-[0_10px_28px_-8px_rgba(47,111,212,0.55)]"
          >
            <span>{t('contributionsPage.mobileNative.ctaSettle')}</span>
            <span className="tabular-nums opacity-90">
              {Number(amountDue).toLocaleString(localeFmt)} FBU
            </span>
          </button>
        </div>
      ) : null}
    </MemberMobileShell>
  );
}
